import { RawMetricsSeries } from 'types/insights';

export type NormalizedSeries = {
  timeData: string[];
  values: number[];
};

const EMPTY_SERIES: NormalizedSeries = { timeData: [], values: [] };

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function toIsoTimestamp(raw: unknown): string | null {
  if (typeof raw === 'string') {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
    return raw;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    // Heuristic: values < 10^12 are seconds, otherwise milliseconds.
    const ms = raw < 1e12 ? raw * 1000 : raw;
    return new Date(ms).toISOString();
  }
  return null;
}

/**
 * Defensive parser for time-series JSON metrics. The exact shape is opaque
 * and may evolve, so we accept several common forms:
 * - { data: [{ timestamp, value }] }
 * - { points: [{ t, v }] } / [{ time, value }]
 * - [[ts, v], ...] tuples
 * - { timestamps: [...], values: [...] } parallel arrays
 * - { timeseries: [{ points: [{ time, value }] }] }
 * Anything we don't recognize falls back to an empty series.
 */
export function normalizeMetricsSeries(raw: RawMetricsSeries | undefined): NormalizedSeries {
  // Gracefully return fallback if empty, uninitialized, or non-object payload received
  if (!raw || typeof raw !== 'object') return EMPTY_SERIES;

  const candidateArrays: unknown[][] = [];
  const obj = raw as Record<string, unknown>;

  // Check if the raw payload is already a flat root array of points
  if (Array.isArray(raw)) candidateArrays.push(raw);
  
  // Safely gather variations of nested array definitions
  if (Array.isArray(obj.data)) candidateArrays.push(obj.data);
  if (Array.isArray(obj.points)) candidateArrays.push(obj.points);
  if (Array.isArray(obj.series)) candidateArrays.push(obj.series);
  if (Array.isArray(obj.results)) candidateArrays.push(obj.results);
  
  if (Array.isArray(obj.timeseries)) {
    for (const entry of obj.timeseries) {
      if (entry && typeof entry === 'object' && Array.isArray((entry as Record<string, unknown>).points)) {
        candidateArrays.push((entry as Record<string, unknown>).points as unknown[]);
      }
    }
  }

  // Iterate down through structural possibilities to evaluate data pairs
  for (const arr of candidateArrays) {
    const result = parseArrayOfPoints(arr);
    if (result.values.length > 0) return result;
  }

  // Fallback parsing engine for dual-aligned index arrays
  if (Array.isArray(obj.timestamps) && Array.isArray(obj.values)) {
    const timestamps = obj.timestamps;
    const values = obj.values;
    const len = Math.min(timestamps.length, values.length);
    const timeData: string[] = [];
    const numericValues: number[] = [];
    
    for (let i = 0; i < len; i += 1) {
      const ts = toIsoTimestamp(timestamps[i]);
      const v = values[i];
      if (ts && isFiniteNumber(v)) {
        timeData.push(ts);
        numericValues.push(v);
      }
    }
    return { timeData, values: numericValues };
  }

  return EMPTY_SERIES;
}

function parseArrayOfPoints(arr: unknown[]): NormalizedSeries {
  const timeData: string[] = [];
  const values: number[] = [];

  for (const point of arr) {
    if (Array.isArray(point) && point.length >= 2) {
      const ts = toIsoTimestamp(point[0]);
      const v = point[1];
      if (ts && isFiniteNumber(v)) {
        timeData.push(ts);
        values.push(v);
      }
      continue;
    }
    if (point && typeof point === 'object') {
      const p = point as Record<string, unknown>;
      const tsRaw =
        p.timestamp ?? p.time ?? p.t ?? p.ts ?? p.date ?? p.x ?? null;
      const vRaw = p.value ?? p.v ?? p.y ?? null;
      const ts = toIsoTimestamp(tsRaw);
      if (ts && isFiniteNumber(vRaw)) {
        timeData.push(ts);
        values.push(vRaw as number);
      }
    }
  }

  return { timeData, values };
}

export function reduceMax(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((acc, v) => (v > acc ? v : acc), values[0]);
}

export function reduceAverage(values: number[]): number | null {
  if (!values.length) return null;
  const sum = values.reduce((acc, v) => acc + v, 0);
  return sum / values.length;
}

export function reduceP95(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95));
  return sorted[idx];
}

/**
 * Render a utilization value as a percentage string.
 * Utilization metrics are commonly fractions (0..1); some pipelines
 * already pre-multiply by 100. We auto-detect using a generous threshold.
 */
export function formatPercent(
  value: number | null | undefined,
  fractionDigits = 1
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  const asPercent = Math.abs(value) <= 1 ? value * 100 : value;
  return `${asPercent.toFixed(fractionDigits)}%`;
}

export function toPercentNumber(value: number): number {
  return Math.abs(value) <= 1 ? value * 100 : value;
}